// src/components/.../AreaDiagram.jsx
import React, { useMemo, useState } from "react";
import { Box, Divider, Typography } from "@mui/material";
import CategoryCard from "./CategoryCard";
import { AREA_PREVIEW_IMAGES } from "./CategoryPreviewImages";

const byLang = (item, lang) =>
  lang === "EN"
    ? item.EN || item.label
    : lang === "JP"
      ? item.JP || item.label
      : item.label;

const ensureSlash = (p) => {
  const s = String(p || "");
  if (!s) return s;
  return s.startsWith("/") ? s : "/" + s.replace(/^\/+/, "");
};

/* ---------------- area overlay buttons (on top of 3D image) ------------- */
const AREA_BUTTONS = [
  // upper part
  {
    key: "pack",
    label: "包裝區",
    EN: "Packaging",
    JP: "包装エリア",
    top: {
      ZH: { xs: "17%", md: "20%", lg: "24%" },
      EN: { xs: "17%", md: "20%", lg: "24%" },
      JP: { xs: "17%", md: "20%", lg: "24%" },
    },
    left: { ZH: { xs: "22%", md: "23%", lg: "32%" } },
    to: "/商品資訊/使用區域/包裝區",
  },
  {
    key: "cook",
    label: "加工調理區",
    EN: "Processing\n/Cooking",
    JP: "加工・調理エリア",
    top: {
      ZH: { xs: "17%", md: "22%", lg: "24%" },
      EN: { xs: "19%", md: "22%", lg: "24%" },
      JP: { xs: "17%", md: "22%", lg: "24%" },
    },
    left: { ZH: { xs: "35%", md: "35%", lg: "40%" } },
    to: "/商品資訊/使用區域/加工|烹飪區",
  },
  {
    key: "pre",
    label: "前處理區",
    EN: "Pre-processing",
    JP: "前処理エリア",
    top: {
      ZH: { xs: "17%", md: "24%", lg: "26%" },
      EN: { xs: "12%", md: "24%", lg: "26%" },
      JP: { xs: "17%", md: "24%", lg: "26%" },
    },
    left: { ZH: { xs: "45%", md: "47.5%", lg: "48.5%" } },
    to: "/商品資訊/使用區域/前處理區",
  },
  {
    key: "rstore",
    label: "原材料\n貯存區",
    EN: "Raw Storage",
    JP: "原料保管",
    top: {
      ZH: { xs: "35%", md: "20%", lg: "24%" },
      EN: { xs: "35%", md: "20%", lg: "24%" },
      JP: { xs: "35%", md: "20%", lg: "24%" },
    },
    left: { ZH: { xs: "22%", md: "58.5%", lg: "56%" } },
    to: "/商品資訊/使用區域/原料儲存區",
  },
  {
    key: "recv",
    label: "原材料\n驗收區",
    EN: "Receiving",
    JP: "原料受け入れ",
    top: {
      ZH: { xs: "35%", md: "25%", lg: "27%" },
      EN: { xs: "35%", md: "25%", lg: "27%" },
      JP: { xs: "35%", md: "25%", lg: "27%" },
    },
    left: { ZH: { xs: "22%", md: "68.5%", lg: "63%" } },
    to: "/商品資訊/使用區域/原料接收區",
  },

  {
    key: "office",
    label: "辦公區",
    EN: "Office",
    JP: "事務室",
    top: {
      ZH: { xs: "32%", md: "15%", lg: "20%" },
      EN: { xs: "32%", md: "15%", lg: "20%" },
      JP: { xs: "32%", md: "15%", lg: "20%" },
    },
    left: {
      ZH: { xs: "81%", md: "78%", lg: "70%" },
      EN: { xs: "81%", md: "78%", lg: "70%" },
      JP: { xs: "81%", md: "78%", lg: "70%" },
    },
    to: "/商品資訊/使用區域/辦公區",
  },

  // bottom part
  {
    key: "fstore",
    label: "成品貯存區",
    EN: "Storage",
    JP: "製品保管",
    top: {
      ZH: { xs: "17%", md: "58%", lg: "62%" },
      EN: { xs: "19%", md: "58%", lg: "62%" },
      JP: { xs: "17%", md: "58%", lg: "62%" },
    },
    left: { ZH: { xs: "59%", md: "20%", lg: "30%" } },
    to: "/商品資訊/使用區域/成品儲存區",
  },
  {
    key: "ship",
    label: "出貨區",
    EN: "Shipping",
    JP: "出荷エリア",
    top: {
      ZH: { xs: "17%", md: "73%", lg: "73%" },
      EN: { xs: "17%", md: "73%", lg: "73%" },
      JP: { xs: "17%", md: "73%", lg: "73%" },
    },
    left: { ZH: { xs: "78%", md: "34%", lg: "39%" } },
    to: "/商品資訊/使用區域/出貨區",
  },
  {
    key: "handwash",
    label: "洗手\n消毒區",
    EN: "Handwash",
    JP: "手洗い場",
    top: {
      ZH: { xs: "59%", md: "62%", lg: "62%" },
      EN: { xs: "59%", md: "62%", lg: "62%" },
      JP: { xs: "59%", md: "62%", lg: "62%" },
    },
    left: { ZH: { xs: "19%", md: "47%", lg: "48.5%" } },
    to: "/商品資訊/使用區域/洗手消毒區",
  },
  {
    key: "clean",
    label: "浴塵室",
    EN: "Air\nShower",
    JP: "クリーンルーム",
    top: {
      ZH: { xs: "45%", md: "36%", lg: "38.5%" },
      EN: { xs: "45%", md: "36%", lg: "38.5%" },
      JP: { xs: "45%", md: "36%", lg: "38.5%" },
    },
    left: { ZH: { xs: "64%", md: "47.5%", lg: "48.5%" } },
    to: "/商品資訊/使用區域/洗手間",
  },
  {
    key: "locker-male",
    label: "更衣室",
    EN: "locker room",
    JP: "更衣室",
    top: {
      ZH: { xs: "66%", md: "53%", lg: "55%" },
      EN: { xs: "66%", md: "53%", lg: "55%" },
      JP: { xs: "66%", md: "53%", lg: "55%" },
    },
    left: { ZH: { xs: "77%", md: "61%", lg: "58%" } },
    to: "/商品資訊/使用區域/員工更衣室",
  },
  {
    key: "locker-female",
    label: "女衣室",
    EN: "locker room",
    JP: "更衣室",
    top: {
      ZH: { xs: "66%", md: "72%", lg: "73%" },
      EN: { xs: "66%", md: "72%", lg: "73%" },
      JP: { xs: "66%", md: "72%", lg: "73%" },
    },
    left: { ZH: { xs: "77%", md: "61%", lg: "58%" } },
    to: "/商品資訊/使用區域/員工更衣室",
  },
  {
    key: "restroom",
    label: "洗手間",
    EN: "restroom",
    JP: "バスルーム",
    top: {
      ZH: { xs: "74%", md: "52%", lg: "54%" },
      EN: { xs: "78%", md: "52%", lg: "54%" },
      JP: { xs: "74%", md: "52%", lg: "54%" },
    },
    left: {
      ZH: { xs: "84%", md: "82%", lg: "72%" },
      EN: { xs: "84%", md: "82%", lg: "72%" },
      JP: { xs: "84%", md: "82%", lg: "72%" },
    },
    to: "/商品資訊/使用區域/洗手間",
  },
  {
    key: "waste",
    label: "廢棄物區",
    EN: "Waste\narea",
    JP: "廃棄物\nエリア",
    top: {
      ZH: { xs: "75%", md: "73%", lg: "75%" },
      EN: { xs: "78%", md: "73%", lg: "75%" },
      JP: { xs: "75%", md: "73%", lg: "75%" },
    },
    left: { ZH: { xs: "18%", md: "83%", lg: "73%" } },
    to: "/商品資訊/使用區域/廢棄物處理區",
  },
];

const FOOD_ITEMS = [
  {
    key: "recv",
    label: "原料接收區",
    EN: "Receiving",
    JP: "原料受け入れ",
    to: "/商品資訊/使用區域/原料接收區",
    img: "/img/products/B2B/foodArea01.webp",
    objectfit: "cover",
  },
  {
    key: "rstore",
    label: "原料儲存區",
    EN: "Raw Storage",
    JP: "原料保管",
    to: "/商品資訊/使用區域/原料儲存區",
    img: "/img/products/B2B/foodArea02.webp",
    objectfit: "cover",
  },
  {
    key: "pre",
    label: "前處理區",
    EN: "Pre-processing",
    JP: "前処理エリア",
    to: "/商品資訊/使用區域/前處理區",
    img: "/img/products/B2B/foodArea03.webp",
    objectfit: "cover",
  },
  {
    key: "cook",
    label: "加工/烹飪區",
    EN: "Processing / Cooking",
    JP: "加工/調理",
    to: "/商品資訊/使用區域/加工|烹飪區",
    img: "/img/products/B2B/foodArea04.webp",
    objectfit: "cover",
  },
  {
    key: "pack",
    label: "包裝區",
    EN: "Packaging",
    JP: "包装エリア",
    to: "/商品資訊/使用區域/包裝區",
    img: "/img/products/B2B/foodArea05.webp",
    objectfit: "cover",
  },
  {
    key: "fstore",
    label: "成品儲存區",
    EN: "Storage",
    JP: "製品保管エリア",
    to: "/商品資訊/使用區域/成品儲存區",
    img: "/img/products/B2B/foodArea06.webp",
    objectfit: "cover",
  },
  {
    key: "ship",
    label: "出貨區",
    EN: "Shipping",
    JP: "出荷エリア",
    to: "/商品資訊/使用區域/出貨區",
    img: "/img/products/B2B/foodArea07.webp",
    objectfit: "cover",
  },
];

const NONFOOD_ITEMS = [
  {
    key: "locker",
    label: "員工更衣室",
    EN: "Changing Room",
    JP: "更衣室",
    to: "/商品資訊/使用區域/員工更衣室",
    img: "/img/products/B2B/nonFoodArea01.webp",
    objectfit: "cover",
  },
  {
    key: "handwash",
    label: "洗手消毒區",
    EN: "Handwash / Disinfection",
    JP: "手洗い・消毒",
    to: "/商品資訊/使用區域/洗手消毒區",
    img: "/img/products/B2B/nonFoodArea02.webp",
    objectfit: "cover",
  },
  {
    key: "airshower",
    label: "浴塵室",
    EN: "Air Shower",
    JP: "エアシャワー室",
    to: "/商品資訊/使用區域/浴塵室",
    img: "/img/products/B2B/nonFoodArea03.webp",
    objectfit: "cover",
  },
  {
    key: "office",
    label: "辦公區",
    EN: "Office",
    JP: "事務室",
    to: "/商品資訊/使用區域/辦公區",
    img: "/img/products/B2B/nonFoodArea04.webp",
    objectfit: "cover",
  },
  {
    key: "restroom",
    label: "洗手／衛生間",
    EN: "Restroom",
    JP: "お手洗い",
    to: "/商品資訊/使用區域/洗手間",
    img: "/img/products/B2B/nonFoodArea05.webp",
    objectfit: "cover",
  },
];

const parsePercentValue = (value, fallback = 50) => {
  const raw =
    typeof value === "object"
      ? value?.md || value?.xs || fallback
      : value || fallback;

  const n = Number(String(raw).replace("%", ""));
  return Number.isFinite(n) ? n : fallback;
};

const getAreaPreviewPlacement = (btn, lang = "ZH") => {
  const leftValue = btn.left?.[lang] || btn.left?.ZH;
  const topValue = btn.top?.[lang] || btn.top?.ZH;

  const leftPercent = parsePercentValue(leftValue, 50);
  const topPercent = parsePercentValue(topValue, 50);

  const openToLeft = leftPercent >= 58;
  const safeTop = Math.min(Math.max(topPercent, 22), 62);

  if (openToLeft) {
    return {
      side: "left",
      sx: {
        top: `${safeTop}%`,
        right: `calc(${100 - leftPercent}% + 6%)`,
        transform: "translateY(-50%)",
      },
    };
  }

  return {
    side: "right",
    sx: {
      top: `${safeTop}%`,
      left: `calc(${leftPercent}% + 6%)`,
      transform: "translateY(-50%)",
    },
  };
};

const renderLabelWithBreaks = (text) => {
  const parts = String(text).split("\n");
  return parts.map((p, idx) => (
    <React.Fragment key={idx}>
      {p}
      {idx < parts.length - 1 && <br />}
    </React.Fragment>
  ));
};

const foodTitle = {
  ZH: "食品處理區域",
  EN: "Food Processing Area",
  JP: "食品處理區域",
};
const nonFoodTitle = {
  ZH: "非食品處理區域",
  EN: "Non-food Processing Area",
  JP: "非食品取扱区域",
};

export default function AreaDiagram({
  lang = "ZH",
  areaTitle,
  selectedHref,
  onSelectCategory,
  cardsOnly = false,
  selectedLayout = false,
}) {
  const [hoveredAreaKey, setHoveredAreaKey] = useState(null);

  const hoveredButton = AREA_BUTTONS.find((btn) => btn.key === hoveredAreaKey);
  const hoveredPreviewItem = hoveredButton
    ? AREA_PREVIEW_IMAGES[hoveredButton.key]
    : null;

  const hoveredPreviewPlacement = hoveredButton
    ? getAreaPreviewPlacement(hoveredButton, lang)
    : null;
  const labelForButton = (btn) =>
    lang === "EN"
      ? btn.EN || btn.label
      : lang === "JP"
        ? btn.JP || btn.label
        : btn.label;

  const pick = (to) => {
    const href = ensureSlash(to);
    onSelectCategory?.(href);

    // ✅ Let ProductInfoPage handle the scroll-to-chip-bar
    window.dispatchEvent(
      new CustomEvent("app:categorySelected", { detail: { to: href } }),
    );
  };

  return (
    <Box>
      {!cardsOnly && (
        <>
          {/* 使用區域 title + 3D area image + overlay text buttons */}
          <Box
            sx={{
              position: "relative",
              width: "100vw",
              ml: "calc(50% - 50vw)",
              overflow: "visible",
              mb: { xs: 0, md: 4 },
              pt: { xs: 2, md: 4 },
              backgroundImage: {
                xs: "none",
                md: 'url("/img/products/B2B/area-bg.jpg")',
              },
              backgroundSize: "100% 100%",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
            }}
          >
            <Box sx={{ width: "100%", maxWidth: 1080, mx: "auto" }}>
              <Divider sx={{ color: "black" }}>
                <Typography
                  align="center"
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: 18, sm: 24, md: 28, lg: 25 },
                  }}
                >
                  {areaTitle}
                </Typography>
              </Divider>
            </Box>

            {/* 3D area image + overlay text buttons */}
            <Box
              sx={{
                position: "relative",
                width: "100%",
                display: { xs: "none", md: "flex" },
                justifyContent: "center",
                overflow: "visible",
                mb: { xs: 3, md: 8 },
              }}
            >
              <Box
                component="img"
                src="/img/products/B2B/area-diagram.webp"
                alt="area"
                loading="eager"
                fetchPriority="medium"
                decoding="async"
                sx={{
                  width: { xs: "100vw", sm: "100vw", md: "75vw", lg: "60vw" },
                  maxWidth: "none",
                  height: "auto",
                  display: "block",
                  objectFit: "cover",
                  transform: {
                    xs: "translateY(-28px)",
                    md: "translateY(-72px)",
                  },
                  mb: { xs: "-28px", md: "-72px" },
                }}
              />

              {AREA_BUTTONS.map((btn) => (
                <Box
                  key={btn.key}
                  onMouseEnter={() => setHoveredAreaKey(btn.key)}
                  onMouseLeave={() => setHoveredAreaKey(null)}
                  onClick={() => pick(btn.to)}
                  sx={{
                    position: "absolute",
                    top: btn.top[lang],
                    left: btn.left[lang] || btn.left["ZH"],
                    transform: "translate(-50%, -50%)",
                    width: btn.width,
                    textAlign: "center",
                    cursor: "pointer",
                    zIndex: 4,
                  }}
                >
                  <Box
                    sx={{
                      px: { xs: 0.8, md: 1.6 },
                      py: { xs: 0.3, md: 0.6 },
                      borderRadius: 2,
                      backgroundColor: "rgba(255,255,255,0.9)",
                      fontFamily: "LINE Sans TW",
                      fontWeight: 700,
                      fontSize: { xs: 5, sm: 5, md: 13 },
                      color: "#333",
                      whiteSpace: "nowrap",
                      transition: "all .18s ease",
                      "&:hover": { backgroundColor: "rgba(255,255,255,1)" },
                    }}
                  >
                    {renderLabelWithBreaks(labelForButton(btn))}
                  </Box>
                </Box>
              ))}
              {hoveredPreviewItem?.img && hoveredPreviewPlacement && (
                <Box
                  sx={{
                    position: "absolute",
                    ...hoveredPreviewPlacement.sx,
                    width: { md: "38vw", lg: "34vw" },
                    maxWidth: 520,
                    minWidth: 320,
                    aspectRatio: "16 / 10",
                    borderRadius: 1.5,
                    overflow: "hidden",
                    backgroundColor: "#fff",
                    boxShadow: "0 14px 36px rgba(0,0,0,0.24)",
                    zIndex: 5,
                    pointerEvents: "none",
                    display: { xs: "none", md: "block" },
                    animation:
                      hoveredPreviewPlacement.side === "left"
                        ? "areaPreviewLeft .18s ease-out"
                        : "areaPreviewRight .18s ease-out",
                    "@keyframes areaPreviewLeft": {
                      from: {
                        opacity: 0,
                        transform: "translate(18px, -50%) scale(0.98)",
                      },
                      to: {
                        opacity: 1,
                        transform: "translate(0, -50%) scale(1)",
                      },
                    },
                    "@keyframes areaPreviewRight": {
                      from: {
                        opacity: 0,
                        transform: "translate(-18px, -50%) scale(0.98)",
                      },
                      to: {
                        opacity: 1,
                        transform: "translate(0, -50%) scale(1)",
                      },
                    },
                  }}
                >
                  <Box
                    component="img"
                    src={hoveredPreviewItem.img}
                    alt={byLang(hoveredPreviewItem, lang)}
                    loading="eager"
                    decoding="async"
                    sx={{
                      width: "100%",
                      height: "100%",
                      display: "block",
                      objectFit: hoveredPreviewItem.objectfit || "cover",
                      objectPosition:
                        hoveredPreviewItem.imgPosition || "center",
                    }}
                  />
                </Box>
              )}
            </Box>
          </Box>
        </>
      )}

      {/* 食品處理區域 */}
      <Box
        sx={{
          position: "relative",
          zIndex: 0,
          pt: selectedLayout ? { xs: 5, md: 8 } : 0,
          "&::before": selectedLayout
            ? {
                content: '""',
                position: "absolute",
                top: 0,
                bottom: 0,
                left: "50%",
                width: "100vw",
                transform: "translateX(-50%)",
                bgcolor: "#F0F4F5",
                zIndex: -1,
              }
            : undefined,
        }}
      >
        <Typography
          sx={{ fontWeight: 700, fontSize: { xs: 16, md: 18 }, mb: 1.5 }}
        >
          {foodTitle[lang]}
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "repeat(2, minmax(0, 1fr))",
              md: "repeat(4, minmax(0, 1fr))",
            },
            gap: 2,
            mb: { xs: 3, md: 8 },
          }}
        >
        {FOOD_ITEMS.map((it) => {
          const label = byLang(it, lang);
          const active = (() => {
            if (!selectedHref) return false;
            const a = decodeURI(selectedHref);
            const b = decodeURI(it.to);
            return a === b || a.startsWith(b + "/");
          })();

          return (
            <CategoryCard
              key={it.key}
              label={label}
              img={it.img}
              active={active}
              onClick={() => pick(it.to)}
              showArrow={it.showArrow ?? true}
              objfit={it.objectfit || "cover"}
              imgPadding={it.imgPadding ?? 0}
              imgBg={it.imgBg ?? "#f7fafc"}
              imgPosition={it.imgPosition || "center"}
              labelWidth="100%"
              labelAlign="left"
            />
          );
        })}
        </Box>
      </Box>

      {/* 非食品處理區域 */}
      <Box
        sx={{
          width: "100vw",
          ml: "calc(50% - 50vw)",
          bgcolor: selectedLayout ? "#fff" : "#F0F4F5",
          py: 2,
          mb: selectedLayout ? 0 : { xs: 3, md: 4 },
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 1080,
            mx: "auto",
            px: { xs: 2, md: 3 },
          }}
        >
          <Typography
            sx={{ fontWeight: 700, fontSize: { xs: 16, md: 18 }, mb: 1.5, pt: 5 }}
          >
            {nonFoodTitle[lang]}
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2, minmax(0, 1fr))",
                md: "repeat(4, minmax(0, 1fr))",
              },
              gap: 2,
              pb: 5
            }}
          >
            {NONFOOD_ITEMS.map((it) => {
              const label = byLang(it, lang);
              const active = (() => {
                if (!selectedHref) return false;
                const a = decodeURI(selectedHref);
                const b = decodeURI(it.to);
                return a === b || a.startsWith(b + "/");
              })();

              return (
                <CategoryCard
                  key={it.key}
                  label={label}
                  img={it.img}
                  active={active}
                  onClick={() => pick(it.to)}
                  showArrow={it.showArrow ?? true}
                  objfit={it.objectfit || "cover"}
                  imgPadding={it.imgPadding ?? 0}
                  imgBg={it.imgBg ?? "#f7fafc"}
                  imgPosition={it.imgPosition || "center"}
                  labelWidth="100%"
                  labelAlign="left"
                />
              );
            })}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
